import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import EastIcon from "@mui/icons-material/East";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { db } from "../../authentication/firebase";
import { CircularProgress } from "@mui/material";

const OngoingProjects = () => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to fetch latest 3 projects from Firebase
  const fetchLatestProjectsFromFirebase = async () => {
    try {
      setLoading(true);

      const projectsCollectionRef = collection(db, "projects");
      // Get latest 3 projects ordered by creation date (most recent first)
      const projectsQuery = query(
        projectsCollectionRef,
        orderBy("createdAt", "desc"),
        limit(3)
      );
      const projectsSnapshot = await getDocs(projectsQuery);

      const fetchedProjects = projectsSnapshot.docs.map((doc) => ({
        firebaseId: doc.id,
        ...doc.data(),
      }));

      setProjects(fetchedProjects);
    } catch (error) {
      console.error("Error fetching latest projects from Firebase:", error);
      setError("Failed to load projects. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestProjectsFromFirebase();
  }, []);

  // Animation variants for smooth scroll transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 80 }, // Start from further below for more dramatic effect
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 50,
        damping: 20,
        duration: 0.8,
      },
    },
    hover: {
      y: -12, // Move up on hover
      scale: 1.03,
      boxShadow: "0 20px 30px rgba(0, 0, 0, 0.12)",
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const infoVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.2,
        duration: 0.5,
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.5,
        duration: 0.5,
      },
    },
    hover: {
      scale: 1.05,
      backgroundColor: "var(--primary-dark-color)",
      transition: {
        duration: 0.3,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress color="inherit" size={50} />
        <LoadingText>Loading projects...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <p>{error}</p>
        <button
          onClick={fetchLatestProjectsFromFirebase}
          className="primary-btn"
        >
          Try Again
        </button>
      </ErrorContainer>
    );
  }

  return (
    <StyledProjects>
      <motion.div
        className="header-container"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={titleVariants}
      >
        <motion.h2 className="title" variants={titleVariants}>
          Today’s efforts for tomorrow’s earth
        </motion.h2>

        <motion.div
          className="title-decoration"
          initial={{ width: 0 }}
          whileInView={{ width: "70px" }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        />

        <motion.p
          className="subtitle"
          variants={titleVariants}
          transition={{ delay: 0.2 }}
        >
          Discover how innovation and impact come together in our active and evolving initiatives.
        </motion.p>
      </motion.div>

      {projects.length === 0 ? (
        <NoProjectsMessage>
          No ongoing projects available at the moment. Check back later for new
          initiatives!
        </NoProjectsMessage>
      ) : (
        <motion.div
          className="projects-container"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }} // Start animation when 10% of container is in view
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.firebaseId || index}
              className={`project-tile ${activeIndex === index ? "active" : ""
                }`}
              variants={cardVariants}
              whileHover="hover"
              whileTap={{ scale: 0.98 }}
              viewport={{ once: true }}
              onHoverStart={() => setActiveIndex(index)}
              onHoverEnd={() => setActiveIndex(null)}
            >
              <div className="image-wrapper">
                <img src={project.img} alt={project.title} />
                <div className="image-overlay" />
                {project.location && (
                  <div className="floating-tag">
                    <LocationOnIcon fontSize="small" />
                    {project.location}
                  </div>
                )}
              </div>

              <div className="content">
                <h3>{project.title}</h3>

                <p className="description">{project.description}</p>

                <motion.div className="project-info" variants={infoVariants}>
                  <div className="info-item">
                    <PeopleAltIcon fontSize="small" />
                    <span>{project.contributors} Contributors</span>
                  </div>

                  <div className="info-item">
                    <CalendarTodayIcon fontSize="small" />
                    <span>{project.startDate}</span>
                  </div>
                </motion.div>

                {/* Project Funding Progress */}
                <motion.div className="funding-progress" variants={infoVariants}>
                  <div className="funding-header">
                    <span className="funding-label">Project Funding</span>
                    <span className="funding-amount">
                      ${(project.contributedAmount || 0).toLocaleString()} / ${(project.budget || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar"
                      style={{
                        width: `${Math.min(((project.contributedAmount || 0) / (project.budget || 1)) * 100, 100)}%`
                      }}
                    />
                    <span className="progress-text">
                      {Math.round(((project.contributedAmount || 0) / (project.budget || 1)) * 100)}% funded
                    </span>
                  </div>
                </motion.div>

                <motion.button
                  className="contribute-button"
                  onClick={() => {
                    // Check if project has a payment link
                    if (project.paymentLink) {
                      // Open the payment link directly
                      window.open(project.paymentLink, '_blank');
                    } else {
                      // Navigate to projects page as fallback
                      navigate("/projects");
                    }
                  }}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Make it happen
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      <motion.div
        className="button-container"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.8 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <motion.button
          className="view-all-button"
          onClick={() => navigate("/projects")}
          whileHover={{
            scale: 1.05,
            boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
          }}
          whileTap={{ scale: 0.95 }}
        >
          <span>View All Projects</span>
          <motion.div
            className="arrow-icon"
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <EastIcon />
          </motion.div>
        </motion.button>
      </motion.div>
    </StyledProjects>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 20px;
  text-align: center;
  width: var(--section-width);
  margin: 0 auto;
`;

const LoadingText = styled.p`
  color: var(--text-color);
  font-size: 1.1rem;
  margin: 0;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 20px;
  text-align: center;
  width: var(--section-width);
  margin: 0 auto;

  p {
    color: var(--text-color);
    font-size: 1.1rem;
    margin: 0;
  }

  button {
    padding: 12px 24px;
    border-radius: 50px;
    border: none;
    background: var(--primary-color);
    color: var(--text-color);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      opacity: 0.9;
    }
  }
`;

const NoProjectsMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-gray-color);
  font-size: 1.2rem;
  background: var(--section-bg-color);
  border-radius: 12px;
  margin: 40px 0;
`;

// Styling (with enhanced transitions)
const StyledProjects = styled.section`
  width: var(--section-width);
  margin: var(--section-margin) auto;
  padding: 3rem 0;

  .header-container {
    text-align: center;
    margin-bottom: 4rem;
    position: relative;
  }

  .title {
    font-size: 2.8rem;
    color: var(--text-color);
    margin-bottom: 1rem;
    font-weight: 700;
  }

  .title-decoration {
    height: 5px;
    background: var(--primary-color);
    margin: 0 auto 1.5rem;
    border-radius: 50px;
  }

  .subtitle {
    font-size: 1.1rem;
    color: var(--text-gray-color);
    max-width: 650px;
    margin: 0 auto;
    line-height: 1.6;
  }

  .projects-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2.5rem;
    margin-bottom: 4rem;
  }

  .project-tile {
    background: white;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1); /* Enhanced smooth transition */
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    will-change: transform; /* Performance optimization */

    &.active {
      .image-overlay {
        opacity: 0.3;
      }
    }
  }

  .image-wrapper {
    position: relative;
    overflow: hidden;

    img {
      width: 100%;
      height: 240px;
      object-fit: cover;
      transition: transform 1.2s ease;
      will-change: transform;
    }

    .image-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.7) 100%
      );
      opacity: 0.6;
      transition: opacity 0.5s ease;
    }

    .floating-tag {
      position: absolute;
      bottom: 15px;
      left: 15px;
      background: var(--primary-color);
      color: var(--text-color);
      font-weight: 600;
      font-size: 0.9rem;
      padding: 0.4rem 0.8rem;
      border-radius: 50px;
      display: flex;
      align-items: center;
      gap: 5px;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      z-index: 2;

      svg {
        font-size: 1rem;
      }
    }
  }

  .project-tile:hover .image-wrapper img {
    transform: scale(1.08);
  }

  .content {
    padding: 1.8rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    h3 {
      font-size: 1.5rem;
      color: var(--text-color);
      margin-bottom: 1rem;
      font-weight: 700;
      line-height: 1.3;
    }

    .description {
      color: var(--text-gray-color);
      font-size: 1rem;
      line-height: 1.6;
      margin-bottom: 1.5rem;
      flex-grow: 1;
    }
  }

  .project-info {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 1.5rem;

    .info-item {
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--text-color);
      font-size: 0.9rem;

      svg {
        color: var(--primary-color);
      }
    }
  }

  .funding-progress {
    margin-bottom: 1.5rem;
    padding: 18px;
    background: linear-gradient(135deg, #f8fffe 0%, #f0f9ff 100%);
    border-radius: 12px;
    border: 1px solid rgba(185, 226, 23, 0.15);
    box-shadow: 0 3px 10px rgba(185, 226, 23, 0.06);
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 2px;
      background: linear-gradient(90deg, transparent, rgba(185, 226, 23, 0.6), transparent);
      transition: left 0.6s ease;
    }

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(185, 226, 23, 0.12);
      border-color: rgba(185, 226, 23, 0.3);

      &::before {
        left: 100%;
      }
    }

    .funding-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 14px;

      .funding-label {
        font-size: 0.92rem;
        font-weight: 700;
        background: linear-gradient(135deg, #2d5016 0%, #4a7c2a 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        display: flex;
        align-items: center;
        gap: 6px;

        &::before {
          content: '💚';
          font-size: 0.9rem;
        }
      }

      .funding-amount {
        font-size: 0.88rem;
        font-weight: 700;
        color: #2d5016;
        padding: 6px 12px;
        border-radius: 12px;
        background-color: rgba(185, 226, 23, 0.15);
        border: 1px solid rgba(185, 226, 23, 0.3);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
    }

    .progress-bar-container {
      position: relative;

      .progress-bar {
        height: 10px;
        background: linear-gradient(135deg, #b9e217 0%, #8bc34a 50%, #689f38 100%);
        border-radius: 6px;
        transition: width 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        margin-bottom: 8px;
        box-shadow: 
          0 2px 6px rgba(139, 195, 74, 0.25),
          inset 0 1px 0 rgba(255, 255, 255, 0.3);
        position: relative;
        
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
          border-radius: 6px;
          animation: shimmer-home 2.5s infinite;
          opacity: 0.8;
        }
      }

      .progress-text {
        font-size: 0.82rem;
        color: #2d5016;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 4px;

        &::before {
          content: '🌱';
          font-size: 0.8rem;
        }
      }
    }

    .progress-bar-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 10px;
      background: linear-gradient(90deg, #e8f5e8 0%, #f0f8ff 100%);
      border-radius: 6px;
      z-index: -1;
      box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
      border: 1px solid rgba(139, 195, 74, 0.15);
    }

    @keyframes shimmer-home {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  }

  .contribute-button {
    background-color: var(--primary-color);
    color: var(--text-color);
    font-weight: 600;
    padding: 0.8rem 1.5rem;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    align-self: flex-start;
    box-shadow: 0 4px 10px rgba(185, 226, 23, 0.3);
  }

  .button-container {
    display: flex;
    justify-content: center;
  }

  .view-all-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 0.8rem 2rem;
    border-radius: 50px;
    background-color: white;
    border: 2px solid var(--primary-color);
    color: var(--text-color);
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1); /* Enhanced smooth transition */
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);

    .arrow-icon {
      display: flex;
      align-items: center;
    }

    &:hover {
      background-color: var(--primary-light-color);
    }
  }

  @media (max-width: var(--bp-laptop)) {
    .projects-container {
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    }

    .title {
      font-size: 2.4rem;
    }
  }

  @media (max-width: var(--bp-tablet)) {
    .projects-container {
      grid-template-columns: 1fr;
      max-width: 550px;
      margin-left: auto;
      margin-right: auto;
    }

    .title {
      font-size: 2.2rem;
    }
  }
`;

export default OngoingProjects;
